/** MCAgendaPanel — event-night run sheet with statuses, notes, current/next highlighting. */
import { useEffect, useState, useCallback } from "react";
import api, { formatErr } from "../lib/api";
import { useEventSocket } from "../lib/ws";
import { useMotif } from "../contexts/MotifContext";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { CalendarClock, ChevronRight, StickyNote } from "lucide-react";
import { T } from "../lib/i18n";

const STATUS_STYLES = {
  UPCOMING: { bg: "var(--motif-accent)", fg: "var(--motif-text-muted)", label: "Upcoming" },
  IN_PROGRESS: { bg: "var(--motif-primary)", fg: "var(--motif-primary-fg)", label: "In Progress" },
  COMPLETE: { bg: "#2e5339", fg: "#fff", label: "Complete" },
};

export default function MCAgendaPanel() {
  const { eventId } = useMotif();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [notesId, setNotesId] = useState(null);
  const [notesText, setNotesText] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/mc/agenda");
      setItems(data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEventSocket(eventId, (msg) => {
    if (msg.type === "agenda_update") refresh();
  });

  const canEdit = user && (user.role === "mc" || user.role === "admin");

  const setStatus = async (item, status) => {
    try {
      await api.put(`/mc/agenda/${item.id}`, { status });
      toast.success(`${STATUS_STYLES[status].label}`);
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const saveNotes = async () => {
    try {
      await api.put(`/mc/agenda/${notesId}`, { notes: notesText });
      toast.success("Notes saved");
      setNotesId(null);
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const advance = async () => {
    const cur = items.find((i) => i.status === "IN_PROGRESS");
    const nxt = items.find((i) => i.status === "UPCOMING");
    try {
      if (cur) await api.put(`/mc/agenda/${cur.id}`, { status: "COMPLETE" });
      if (nxt) await api.put(`/mc/agenda/${nxt.id}`, { status: "IN_PROGRESS" });
      toast.success(nxt ? `Now: ${nxt.time_label}` : "Agenda complete");
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
  };

  // Current: explicit IN_PROGRESS first, else derive from clock
  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
  const current = items.find((i) => i.status === "IN_PROGRESS")
    || items.find((i) => i.status !== "COMPLETE" && nowMin >= i.start_minutes && nowMin < i.end_minutes);
  const next = items.find((i) => i.status === "UPCOMING" && i.id !== current?.id);

  return (
    <div className="card-elev p-4" data-testid="mc-agenda-panel">
      <div className="flex items-center gap-2 mb-3">
        <CalendarClock className="w-4 h-4 text-primary-motif" />
        <div className="font-display text-lg text-motif">{T("MC Agenda")}</div>
        {canEdit && (
          <button onClick={advance} className="btn-primary text-[11px] px-3 py-1.5 ml-auto flex items-center gap-1" data-testid="mc-agenda-advance">
            Advance <ChevronRight className="w-3 h-3" />
          </button>
        )}
      </div>

      {(current || next) && (
        <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
          <div className="p-2 rounded-lg" style={{ background: "var(--motif-primary)", color: "var(--motif-primary-fg)" }} data-testid="mc-agenda-current">
            <div className="text-[9px] tracking-luxe opacity-80">NOW</div>
            <div className="font-display">{current ? `${current.time_label}` : "—"}</div>
            <div className="text-[10px] leading-snug line-clamp-2">{current?.text || "Nothing in progress"}</div>
          </div>
          <div className="p-2 rounded-lg bg-accent-motif" data-testid="mc-agenda-next">
            <div className="text-[9px] tracking-luxe text-motif-muted">{T("NEXT")}</div>
            <div className="font-display text-motif">{next ? next.time_label : "—"}</div>
            <div className="text-[10px] text-motif-muted leading-snug line-clamp-2">{next?.text || "End of agenda"}</div>
          </div>
        </div>
      )}

      <div className="space-y-1.5 max-h-[50vh] overflow-y-auto pr-1">
        {items.map((it) => {
          const st = STATUS_STYLES[it.status] || STATUS_STYLES.UPCOMING;
          const isCurrent = current?.id === it.id;
          return (
            <div key={it.id}
              className="p-2.5 rounded-xl"
              style={{
                background: "var(--motif-accent)",
                outline: isCurrent ? "2px solid var(--motif-primary)" : "none",
                opacity: it.status === "COMPLETE" ? 0.55 : 1,
              }}
              data-testid={`mc-agenda-item-${it.order}`}>
              <div className="flex items-start gap-2">
                <div className="font-display text-sm text-primary-motif whitespace-nowrap flex-shrink-0 w-28">{it.time_label}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-motif leading-snug">{it.text}</div>
                  {it.notes && (
                    <div className="text-[10px] text-motif-muted italic mt-0.5 flex items-start gap-1">
                      <StickyNote className="w-3 h-3 flex-shrink-0 mt-0.5" /> {it.notes}
                    </div>
                  )}
                </div>
                <span className="text-[8px] tracking-luxe px-1.5 py-0.5 rounded-full flex-shrink-0"
                  style={{ background: st.bg, color: st.fg }} data-testid={`mc-agenda-status-${it.order}`}>
                  {st.label.toUpperCase()}
                </span>
              </div>
              {canEdit && (
                <div className="flex gap-1 mt-1.5 flex-wrap">
                  {Object.keys(STATUS_STYLES).map((s) => (
                    it.status !== s && (
                      <button key={s} onClick={() => setStatus(it, s)}
                        className="btn-ghost text-[9px] px-1.5 py-0.5"
                        data-testid={`mc-agenda-set-${it.order}-${s.toLowerCase()}`}>
                        {STATUS_STYLES[s].label}
                      </button>
                    )
                  ))}
                  {notesId === it.id ? (
                    <span className="flex gap-1 flex-1 min-w-[200px]">
                      <input className="input-motif text-[10px] flex-1 py-0.5" value={notesText}
                        onChange={(e) => setNotesText(e.target.value)} autoFocus
                        data-testid={`mc-agenda-notes-input-${it.order}`} />
                      <button onClick={saveNotes} className="btn-primary text-[9px] px-1.5" data-testid={`mc-agenda-notes-save-${it.order}`}>{T("Save")}</button>
                    </span>
                  ) : (
                    <button onClick={() => { setNotesId(it.id); setNotesText(it.notes || ""); }}
                      className="btn-ghost text-[9px] px-1.5 py-0.5 ml-auto" data-testid={`mc-agenda-notes-${it.order}`}>
                      {it.notes ? "Edit note" : "Add note"}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {items.length === 0 && <div className="text-motif-muted text-sm italic">{T("No agenda items.")}</div>}
      </div>
    </div>
  );
}
